import { useEffect, useState, useRef } from 'react';

import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import PageviewIcon from '@mui/icons-material/Pageview';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import styled from '@mui/material/styles/styled';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useFileLogger from '../../../../hooks/useFileLogger';
import Modal from '../../../sections/modal/Modal';
import Editor from '../../editor/Editor';

const StyledLogButtonsWrapper = styled('div')({
  display: 'flex',
  gap: '16px',
  padding: '0 24px',
  paddingBottom: '16px',
});

const StyledEditorWrapper = styled('div')({
  '& .CodeMirror': {
    minHeight: '400px',
  },
});

const ViewLogsButton = ({ loading, onDownload, onViewLogs, selectedFiles }) => {
  const editorComponentRef = useRef(null);

  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const toggleModal = () => setOpen(!open);

  const {
    startFetchingLogPartition,
    stopFetchingLogPartitions,
    fetchLatestLogPartition,
    resetLogPartition,
    partitionContent,
    isFetchingLogPartitions,
    endOfFileMessage,
  } = useFileLogger(selectedFiles[0], onViewLogs);

  // Operations on log modal open/close
  useEffect(() => {
    if (open) {
      startFetchingLogPartition();
    } else {
      stopFetchingLogPartitions();
      resetLogPartition();
    }
  }, [open]);

  const scrollToBottom = () => {
    if (editorComponentRef.current) {
      editorComponentRef.current.scrollToBottom();
    }
  };

  return (
    <>
      <Button
        startIcon={<PageviewIcon />}
        onClick={toggleModal}
        size="small"
        variant="text"
        color="inherit"
        disabled={
          loading ||
          selectedFiles.length !== 1 ||
          selectedFiles[0].ext !== 'log'
        }
      >
        {t('buttons.viewLogs', { ns: 'fileBrowser' })}
      </Button>
      <Modal
        openModal={open}
        handleClose={toggleModal}
        title={selectedFiles[0]?.name || ''}
        fullWidth={true}
        maxWidth="lg"
        fullHeight={true}
      >
        <StyledLogButtonsWrapper>
          {isFetchingLogPartitions ? (
            <Button
              color="error"
              onClick={stopFetchingLogPartitions}
              aria-label="pause"
            >
              {t('pause', { ns: 'buttons' })}
              <PauseIcon />
            </Button>
          ) : (
            <Button
              color={'success'}
              onClick={startFetchingLogPartition}
              aria-label="resume"
              disabled={!!endOfFileMessage}
            >
              {t('resume', { ns: 'buttons' })}
              <PlayArrowIcon />
            </Button>
          )}

          <Button
            variant={'outlined'}
            onClick={fetchLatestLogPartition}
            disabled={isFetchingLogPartitions}
            aria-label="Go live"
          >
            {t('goLive', { ns: 'buttons' })}
          </Button>

          <Button onClick={onDownload}>
            {t('download', { ns: 'buttons' })}
          </Button>
          <Tooltip title={t('scrollToBottom', { ns: 'tooltips' })}>
            <IconButton
              onClick={scrollToBottom}
              aria-label="delete"
              style={{ marginLeft: 'auto' }}
            >
              <KeyboardDoubleArrowDownIcon />
            </IconButton>
          </Tooltip>
        </StyledLogButtonsWrapper>
        <StyledEditorWrapper>
          <Editor
            mode={'text/x-verilog'}
            code={`${partitionContent} ${endOfFileMessage}`}
            readOnly={true}
            navigation={false}
            ref={editorComponentRef}
          />
        </StyledEditorWrapper>
      </Modal>
    </>
  );
};

ViewLogsButton.propTypes = {
  loading: PropTypes.bool,
  onDownload: PropTypes.func.isRequired,
  onViewLogs: PropTypes.func.isRequired,
  selectedFiles: PropTypes.array.isRequired,
};

export default ViewLogsButton;
